import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import { useCallback } from 'react'
import { Link } from 'react-router'
import { trpc, trpcClient } from 'shared/api'
import { Alert, AlertTitle } from 'shared/components/ui/alert'
import { Button } from 'shared/components/ui/button'
import { useTranslate } from 'shared/lib/i18n'
import { useAuthStore } from 'shared/store/auth'
import { CashBagCard } from './CashBagCard'
import { Toolbar } from './Toolbar'
import { usePointOfPage } from './use-point-of-page'

/**
 * «Сумка наличных» — the second page under «Касса» (ADR-0036, ADR-0047): the Point's
 * Cashbox per partner, the payments since the last bag, and «Упаковать сумку». The
 * operator packs at their own Point within their open Shift; with none open — or
 * another operator's — the page says so at the top, with the way to «Смена» (the
 * server still decides, and refuses a pack without one). The admin picks a Point
 * and reads; packing is a counter act.
 */
export function CashBagPage() {
	const { t } = useTranslate()
	const queryClient = useQueryClient()
	const isOperator = useAuthStore((s) => s.user.role) === 'operator'
	const { locationId, scoped } = usePointOfPage()
	const shift = useQuery({
		...trpc.shifts.Current.queryOptions({ pointId: locationId }),
		enabled: scoped && isOperator,
	}).data
	const prepare = useCallback(() => trpcClient.cashBags.Prepare.query({ pointId: locationId }), [locationId])

	if (!scoped) return <Toolbar />

	const reason = !isOperator || !shift || shift.mine ? null : shift.shift ? 'not_shift_operator_to_bag' : 'no_open_shift_to_bag'
	const notice = reason && (
		<Alert variant='destructive' className='flex items-center gap-3' data-testid='cash-bag-no-shift' data-reason={reason}>
			<AlertTriangle />
			<AlertTitle className='flex-1'>{t(`cashBagRefusal.${reason}`)}</AlertTitle>
			<Button asChild variant='outline' size='sm' data-testid='cash-bag-to-shift'>
				<Link to='/money'>{t('cashBag.toShift')}</Link>
			</Button>
		</Alert>
	)

	return (
		<CashBagCard
			key={locationId ?? 'own'}
			prepare={prepare}
			packs={isOperator}
			notice={notice}
			onPacked={() => void queryClient.invalidateQueries()}
			toolbar={(action) => <Toolbar action={action} />}
		/>
	)
}
